import { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Form } from "@unform/web";
import { z } from "zod";
import getValidationErrors from "../../../utils/getZodValidationErrors";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { validateSingleFieldOnBlur } from "../../../utils/zodFormValidations";

import {
  ActionContainer,
  BadgeContainer,
  Container,
  Session,
  SessionContainer,
  SessionDivider,
  SessionTitle,
  Horizontal,
  Divider,
  FileDropzoneContainer,
  Label,
  LabelContainer,
  UploadAreaWithLabel,
  HorizontalUpload,
} from "./styles";
import { parse, format } from "date-fns";
import { useToast } from "../../../hooks/toast";
import { useLoading } from "../../../hooks/loading";
import api from "../../../service";
import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import Dropzone from "../../../components/Dropzone";
import InputWithLabel from "../../../components/InputWithLabel";
import TextareaWithLabelAndEditor from "../../../newUXComponents/TextareaWithLabelAndEditor";
import DatePickerWithLabel from "../../../components/DatePickerAntdWithLabel";
import Toggle from "../../../components/Toggle";
import { FieldsContainer, Title, TitleContainer } from "../styles";
import Badge from "../../../components/Badge";
import Switch from "../../../components/Switch";

const initialValues = {
  descriptionPt: "",
  howToDoPt: "",
  linkPt: "",
  descriptionEn: "",
  howToDoEn: "",
  linkEn: "",
  position: "",
  forExhibitor: true,
  forServiceProvider: true,
  forSponsor: true,
  forDigital: true,
  expiresIn: "",
  mediaFileEn: null,
  mediaFilePt: null,
  blockUploadMediaPt: true,
  blockUploadMediaEn: true,
};

const ChecklistItem = () => {
  const formRef = useRef(null);
  const [data, setData] = useState(initialValues);
  const [isUploadDisabledMediaPt, setIsUploadDisabledMediaPt] = useState(false);
  const [isUploadDisabledMediaEn, setIsUploadDisabledMediaEn] = useState(false);
  const [formKey, setFormKey] = useState();
  const [toggleControl, setToggleControl] = useState({
    exhibitor: true,
    serviceProvider: true,
    sponsor: true,
    digital: true,
  });
  const intl = useIntl();
  const { addToast } = useToast();
  const { token } = useSelector((state) => state.auth);
  const { id, itemId } = useParams();
  const { handleLoading } = useLoading();
  const history = useHistory();

  const getValidationSchema = useCallback(
    (requiredEnglish) => {
      return z.object({
        descriptionPt: z
          .string()
          .min(1, intl.formatMessage({ id: "error.field_required" }))
          .max(150, intl.formatMessage({ id: "error.max_length" }, { length: 150 })),
        howToDoPt: z
          .string()
          .min(1, intl.formatMessage({ id: "error.field_required" })),
        linkPt: z
          .string()
          .refine(
            (val) => {
              if (val === "" || val === null || val === undefined) return true;
              try {
                new URL(val);
                return true;
              } catch {
                return false;
              }
            },
            { message: intl.formatMessage({ id: "error.valid_url" }) },
          )
          .optional(),
        descriptionEn: requiredEnglish
          ? z
              .string()
              .min(1, intl.formatMessage({ id: "error.field_required" }))
              .max(150, intl.formatMessage({ id: "error.max_length" }, { length: 150 }))
          : z
              .string()
              .max(150, intl.formatMessage({ id: "error.max_length" }, { length: 150 }))
              .optional(),
        howToDoEn: requiredEnglish
          ? z
              .string()
              .min(1, intl.formatMessage({ id: "error.field_required" }))
          : z.string().optional(),
        linkEn: z
          .string()
          .refine(
            (val) => {
              if (val === "" || val === null || val === undefined) return true;
              try {
                new URL(val);
                return true;
              } catch {
                return false;
              }
            },
            { message: intl.formatMessage({ id: "error.valid_url" }) },
          )
          .optional(),
        position: z
          .string()
          .regex(
            /^(^$|([0-9]*))$/g,
            intl.formatMessage({ id: "error.need_to_be_number" }),
          )
          .optional(),
        forExhibitor: z.boolean(),
        forServiceProvider: z.boolean(),
        forSponsor: z.boolean(),
        forDigital: z.boolean(),
        expiresIn: z
          .any()
          .refine((val) => val !== null && val !== undefined && val !== "", {
            message: intl.formatMessage({ id: "error.field_required" }),
          }),
      });
    },
    [intl],
  );
  const [requiredEnglish, setRequiredEnglish] = useState(false);

  useEffect(() => {
    if (itemId) {
      handleLoading(true);
      api
        .get(`/event-checklist/${itemId}`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        })
        .then(async (result) => {
          if (result.data.output) {
            const resData = result.data.output;

            setData({
              ...resData,
              howToDoEn: resData.howToDoEn === "null" ? "" : resData.howToDoEn,
              expiresIn: parseStringToDate(resData.expiresIn),
            });

            if (resData.descriptionEn === "" && resData.howToDoEn === "null")
              setRequiredEnglish(false);
            else setRequiredEnglish(true);

            setToggleControl({
              exhibitor: resData.forExhibitor,
              serviceProvider: resData.forServiceProvider,
              sponsor: resData.forSponsor,
              digital: resData.forDigital,
            });
          }
          setFormKey(new Date());
          setIsUploadDisabledMediaPt(true);
          setIsUploadDisabledMediaEn(true);
        })
        .catch((err) => {
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response?.data?.code,
            }),
          });
        })
        .finally(() => {
          handleLoading(false);
        });
    }
  }, [addToast, intl, itemId, token, handleLoading]);

  const formatDate = useCallback((date, formatString) => {
    if (!date || date === "") {
      return null;
    }

    return format(date, formatString);
  }, []);

  const generateFormData = useCallback(
    (data, requiredEnglish) => {
      let formData = new FormData();

      const formattedDate = formatDate(data.expiresIn, "yyyy-MM-dd");
      if (data.position !== "") formData.set("position", data.position);
      if (data.linkPt !== "") formData.set("linkPt", data.linkPt);
      if (data.linkEn !== "" && requiredEnglish)
        formData.set("linkEn", data.linkEn);
      formData.set("descriptionPt", data.descriptionPt);
      formData.set("descriptionEn", requiredEnglish ? data.descriptionEn : "");
      formData.set("howToDoPt", data.howToDoPt);
      formData.set(
        "howToDoEn",
        requiredEnglish ? data.howToDoEn || null : null,
      );
      formData.set("forExhibitor", data.forExhibitor);
      formData.set("forServiceProvider", data.forServiceProvider);
      formData.set("forSponsor", data.forSponsor);
      formData.set("forDigital", data.forDigital);
      formData.set("expiresIn", formattedDate);

      if (
        !!data.mediaFileEn[0] &&
        !isUploadDisabledMediaEn &&
        requiredEnglish
      ) {
        formData.set("mediaFileEn", data.mediaFileEn[0]);
        if (itemId) formData.set("hasRemovedOldMediaFileEn", true);
      }

      if (!requiredEnglish && itemId) {
        formData.set("hasRemovedOldMediaFileEn", true);
      }

      if (!!data.mediaFilePt[0] && !isUploadDisabledMediaPt) {
        formData.set("mediaFilePt", data.mediaFilePt[0]);
        if (itemId) formData.set("hasRemovedOldMediaFilePt", true);
      }

      formData.set("eventId", id);

      return formData;
    },
    [
      formatDate,
      itemId,
      isUploadDisabledMediaEn,
      isUploadDisabledMediaPt,
      id,
    ],
  );

  const postChecklistItem = useCallback(
    async (data) => {
      try {
        handleLoading(true);

        const schema = getValidationSchema(requiredEnglish);
        schema.parse(data);
        const formData = generateFormData(data, requiredEnglish);

        api
          .post(`/event-checklist`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              Accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          })
          .then((data) => {
            handleLoading(false);
            addToast({
              type: "success",
              title: intl.formatMessage({
                id: "success",
              }),
              description: intl.formatMessage({
                id: "success.checklist_item_created",
              }),
            });
            history.push(`/event-checklist/${id}`);
          })
          .catch((err) => {
            handleLoading(false);
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            });
          });
      } catch (err) {
        handleLoading(false);
        if (err instanceof z.ZodError) {
          const schema = getValidationSchema(requiredEnglish);
          const errors = getValidationErrors(err);
          Object.keys(errors).forEach((key) => {
            validateSingleFieldOnBlur(errors, schema, key, formRef);
          });
          formRef.current?.setErrors(errors);
          return;
        }
      }
    },
    [
      addToast,
      history,
      intl,
      getValidationSchema,
      requiredEnglish,
      generateFormData,
      token,
      handleLoading,
      id,
    ],
  );

  const putChecklistItem = useCallback(
    async (data) => {
      try {
        handleLoading(true);

        const schema = getValidationSchema(requiredEnglish);
        schema.parse(data);
        const formData = generateFormData(data, requiredEnglish);

        api
          .put(`/event-checklist/${itemId}`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              Accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          })
          .then((data) => {
            handleLoading(false);
            addToast({
              type: "success",
              title: intl.formatMessage({
                id: "success",
              }),
              description: intl.formatMessage({
                id: "success.checklist_item_updated",
              }),
            });
            history.push(`/event-checklist/${id}`);
          })
          .catch((err) => {
            handleLoading(false);
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            });
          });
      } catch (err) {
        handleLoading(false);
        if (err instanceof z.ZodError) {
          const schema = getValidationSchema(requiredEnglish);
          const errors = getValidationErrors(err);
          Object.keys(errors).forEach((key) => {
            validateSingleFieldOnBlur(errors, schema, key, formRef);
          });
          formRef.current?.setErrors(errors);
          return;
        }
      }
    },
    [
      addToast,
      history,
      intl,
      getValidationSchema,
      requiredEnglish,
      generateFormData,
      token,
      handleLoading,
      id,
      itemId,
    ],
  );

  const handleSubmit = useMemo(
    () => (itemId ? putChecklistItem : postChecklistItem),
    [itemId, putChecklistItem, postChecklistItem],
  );

  const onChangeToggle = (key, value) => {
    const { exhibitor, serviceProvider, sponsor, digital } = toggleControl;
    const moreThanOneToggleSelected =
      [exhibitor, serviceProvider, sponsor, digital].filter((value) => value)
        .length > 1;

    if (value || moreThanOneToggleSelected) {
      setToggleControl({
        ...toggleControl,
        [key]: value,
      });
    } else {
      if (key === "exhibitor") {
        formRef.current.setFieldValue("forServiceProvider", "true");
        setToggleControl({
          ...toggleControl,
          [key]: value,
          serviceProvider: true,
        });
      } else {
        formRef.current.setFieldValue("forExhibitor", "true");
        setToggleControl({
          ...toggleControl,
          [key]: value,
          exhibitor: true,
        });
      }
    }
  };

  const onChangeUploadMediaPtToggle = (e) => {
    setIsUploadDisabledMediaPt(!e.target.checked);
  };

  const onChangeUploadMediaEnToggle = (e) => {
    setIsUploadDisabledMediaEn(!e.target.checked);
  };

  const validationBlur = (name) => {
    let errors = formRef.current.getErrors();
    const schema = getValidationSchema(requiredEnglish);

    validateSingleFieldOnBlur(errors, schema, name, formRef);
  };

  const pushBack = () => {
    history.push(`/event-checklist/${id}`);
  };

  const parseStringToDate = (dateString) => {
    if (!dateString) {
      return null;
    }
    return parse(dateString, "yyyy-MM-dd", new Date());
  };

  const pageTitleLabel = useMemo(
    () => (itemId ? "checklist.edit" : "checklist.new"),
    [itemId],
  );

  const handleRequiredEnglish = (required) => {
    setRequiredEnglish(required);

    // Only clear stale "required" errors when English is turned OFF. When
    // turning it back ON, don't proactively flag empty fields as errored —
    // let the normal onBlur validation surface that if the user actually
    // leaves them empty, same as any other field.
    if (required) return;

    const schema = getValidationSchema(required);
    const formData = formRef.current.getData();
    const currentErrors = formRef.current.getErrors();

    // setErrors() replaces the form's whole error state, and formRef doesn't
    // reflect it synchronously — calling it once per field (each reading
    // getErrors() again) makes a later field's update stomp on an earlier
    // one's. Accumulate both fields' results and set them in a single call.
    const updatedErrors = { ...currentErrors };
    ["descriptionEn", "howToDoEn"].forEach((name) => {
      const result = schema.safeParse({ [name]: formData[name] });
      const fieldError = result.success
        ? undefined
        : result.error.errors.find((error) => error.path[0] === name);
      updatedErrors[name] = fieldError ? fieldError.message : undefined;
    });

    formRef.current.setErrors(updatedErrors);
  };

  return (
    <AnimatedMainContainer>
      <Container>
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          initialData={{
            descriptionPt: data.descriptionPt.substring(0, 150),
            howToDoPt: data.howToDoPt,
            linkPt: data.linkPt,
            descriptionEn: data.descriptionEn.substring(0, 150),
            howToDoEn: data.howToDoEn,
            linkEn: data.linkEn,
            position: data.position,
            forExhibitor: data.forExhibitor,
            forServiceProvider: data.forServiceProvider,
            forSponsor: data.forSponsor,
            forDigital: data.forDigital,
            expiresIn: data.expiresIn,
          }}
          key={formKey}
        >
          <TitleContainer>
            <Title>
              <FormattedMessage id={pageTitleLabel} />
            </Title>
            <ActionContainer>
              <BadgeContainer onClick={() => pushBack()}>
                <Badge
                  color="#C9C9C9"
                  text={intl.formatMessage({ id: "goback" })}
                  fontSize="12px"
                />
              </BadgeContainer>
              <BadgeContainer onClick={() => formRef.current.submitForm()}>
                <Badge
                  color="#002244"
                  text={intl.formatMessage({ id: "save" })}
                  fontSize="12px"
                />
              </BadgeContainer>
            </ActionContainer>
          </TitleContainer>
          <SessionDivider nr_items="2">
            <Session>
              <SessionContainer>
                <SessionTitle>
                  <FormattedMessage id="portuguese" />
                </SessionTitle>
              </SessionContainer>
              <FieldsContainer>
                <InputWithLabel
                  name="descriptionPt"
                  validationBlur={validationBlur}
                  placeholder={intl.formatMessage({
                    id: "checklist.item_description_pt",
                  })}
                  label={intl.formatMessage({
                    id: "checklist.item_description_pt",
                  })}
                  maxlength={150}
                  required
                />
                <TextareaWithLabelAndEditor
                  name="howToDoPt"
                  placeholder={intl.formatMessage({ id: "checklist.howto_pt" })}
                  label={intl.formatMessage({ id: "checklist.howto_pt" })}
                  validationBlur={validationBlur}
                  className={"textArea"}
                  required
                />
                <InputWithLabel
                  name="linkPt"
                  validationBlur={validationBlur}
                  placeholder={intl.formatMessage({ id: "checklist.link_pt" })}
                  label={intl.formatMessage({ id: "checklist.link_pt" })}
                />
              </FieldsContainer>
            </Session>

            <Session>
              <SessionContainer>
                <SessionTitle>
                  <FormattedMessage id="english" />
                </SessionTitle>

                <Switch
                  onChange={(required) => handleRequiredEnglish(required)}
                  defaultChecked={requiredEnglish}
                />
              </SessionContainer>

              <FieldsContainer>
                <InputWithLabel
                  name="descriptionEn"
                  validationBlur={validationBlur}
                  placeholder={intl.formatMessage({
                    id: "checklist.item_description_en",
                  })}
                  label={intl.formatMessage({
                    id: "checklist.item_description_en",
                  })}
                  maxlength={150}
                  required={requiredEnglish}
                  disabled={!requiredEnglish}
                />
                <TextareaWithLabelAndEditor
                  name="howToDoEn"
                  placeholder={intl.formatMessage({ id: "checklist.howto_en" })}
                  label={intl.formatMessage({ id: "checklist.howto_en" })}
                  validationBlur={validationBlur}
                  className={"textArea"}
                  required={requiredEnglish}
                  disabled={!requiredEnglish}
                  readOnly={!requiredEnglish}
                />
                <InputWithLabel
                  name="linkEn"
                  validationBlur={validationBlur}
                  placeholder={intl.formatMessage({ id: "checklist.link_en" })}
                  label={intl.formatMessage({ id: "checklist.link_en" })}
                  disabled={!requiredEnglish}
                />
              </FieldsContainer>
            </Session>
          </SessionDivider>
          <HorizontalUpload>
            <UploadAreaWithLabel>
              {itemId && (
                <Toggle
                  name="blockUploadMediaPt"
                  label={intl.formatMessage({ id: "checklist.reUploadFiles" })}
                  onChange={onChangeUploadMediaPtToggle}
                />
              )}
              <LabelContainer>
                <Label>{intl.formatMessage({ id: "checklist.mediaPT" })}</Label>
              </LabelContainer>
              <FileDropzoneContainer disabled={isUploadDisabledMediaPt}>
                <Dropzone
                  name="mediaFilePt"
                  accept={[
                    ".jpg",
                    ".jpeg",
                    ".pdf",
                    ".doc",
                    ".txt",
                    ".png",
                    ".mp3",
                  ]}
                  type="PDF/JPG/JPEG/PNG/DOC/TXT/MP3"
                  multiple={false}
                  disabled={isUploadDisabledMediaPt}
                  maxSize={10000000}
                />
              </FileDropzoneContainer>
            </UploadAreaWithLabel>
            <UploadAreaWithLabel>
              {itemId && (
                <Toggle
                  name="blockUploadMediaEn"
                  label={intl.formatMessage({ id: "checklist.reUploadFiles" })}
                  onChange={onChangeUploadMediaEnToggle}
                  disabled={!requiredEnglish}
                />
              )}
              <LabelContainer>
                <Label>{intl.formatMessage({ id: "checklist.mediaEN" })}</Label>
              </LabelContainer>
              <FileDropzoneContainer
                disabled={isUploadDisabledMediaEn || !requiredEnglish}
              >
                <Dropzone
                  name="mediaFileEn"
                  accept={[
                    ".jpg",
                    ".jpeg",
                    ".pdf",
                    ".doc",
                    ".txt",
                    ".png",
                    ".mp3",
                  ]}
                  type="PDF/JPG/JPEG/PNG/DOC/TXT/MP3"
                  multiple={false}
                  disabled={isUploadDisabledMediaEn || !requiredEnglish}
                  maxSize={10000000}
                />
              </FileDropzoneContainer>
            </UploadAreaWithLabel>
          </HorizontalUpload>
          <Divider />
          <Session>
            <FieldsContainer>
              <Horizontal>
                <Horizontal>
                  <DatePickerWithLabel
                    name="expiresIn"
                    validationBlur={validationBlur}
                    label={intl.formatMessage({ id: "checklist.expires" })}
                    labelOrientation="vertical"
                    required
                  />
                  <InputWithLabel
                    name="position"
                    validationBlur={validationBlur}
                    placeholder={intl.formatMessage({
                      id: "checklist.position",
                    })}
                    label={intl.formatMessage({ id: "checklist.position" })}
                  />
                </Horizontal>
                <Horizontal>
                  <Toggle
                    name="forExhibitor"
                    label={intl.formatMessage({ id: "checklist.exhibitor" })}
                    onChange={(event) =>
                      onChangeToggle("exhibitor", event.target.checked)
                    }
                  />
                  <Toggle
                    name="forServiceProvider"
                    label={intl.formatMessage({ id: "checklist.provider" })}
                    onChange={(event) =>
                      onChangeToggle("serviceProvider", event.target.checked)
                    }
                  />
                  <Toggle
                    name="forSponsor"
                    label={intl.formatMessage({ id: "checklist.sponsor" })}
                    onChange={(event) =>
                      onChangeToggle("sponsor", event.target.checked)
                    }
                  />
                  <Toggle
                    name="forDigital"
                    label={intl.formatMessage({ id: "checklist.digital" })}
                    onChange={(event) =>
                      onChangeToggle("digital", event.target.checked)
                    }
                  />
                </Horizontal>
              </Horizontal>
            </FieldsContainer>
          </Session>
        </Form>
      </Container>
    </AnimatedMainContainer>
  );
};

export default ChecklistItem;
